import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  InputNumber,
  Upload,
  message,
  Image,
  Popconfirm,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import adminApiService from '../../services/adminApi';

interface Banner {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  image: string;
  link: string;
  sortOrder: number;
  active: boolean;
}

const BannersPage: React.FC = () => {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingBanner, setEditingBanner] = useState<Banner | null>(null);
  const [form] = Form.useForm();
  const [uploadedImagePath, setUploadedImagePath] = useState<string>('');

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    setLoading(true);
    try {
      const response = await adminApiService.getData('banners');
      setBanners(response.data.banners || []);
    } catch (error) {
      message.error('加载 Banner 数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingBanner(null);
    form.resetFields();
    setUploadedImagePath('');
    setModalVisible(true);
  };

  const handleEdit = (banner: Banner) => {
    setEditingBanner(banner);
    form.setFieldsValue(banner);
    setUploadedImagePath(banner.image);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      const newBanners = banners.filter((b) => b.id !== id);
      await adminApiService.updateData('banners', { banners: newBanners });
      message.success('删除成功');
      loadBanners();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const newBanner: Banner = {
        ...values,
        id: editingBanner?.id || String(Date.now()),
        image: uploadedImagePath || values.image,
      };

      let newBanners: Banner[];
      if (editingBanner) {
        newBanners = banners.map((b) => (b.id === editingBanner.id ? newBanner : b));
      } else {
        newBanners = [...banners, newBanner];
      }

      await adminApiService.updateData('banners', { banners: newBanners });
      message.success(editingBanner ? '更新成功' : '添加成功');
      setModalVisible(false);
      loadBanners();
    } catch (error) {
      message.error('保存失败');
    }
  };

  const handleUpload = async (file: File) => {
    try {
      const response = await adminApiService.uploadFile(file, 'images', 'banners');
      setUploadedImagePath(response.path);
      message.success('图片上传成功');
      return false;
    } catch (error) {
      message.error('图片上传失败');
      return false;
    }
  };

  const handleSaveAll = async () => {
    try {
      await adminApiService.updateData('banners', { banners });
      message.success('保存成功');
    } catch (error) {
      message.error('保存失败');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '图片',
      dataIndex: 'image',
      key: 'image',
      width: 120,
      render: (image: string) => (
        <Image src={image} alt="banner" className="image-preview" />
      ),
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: '副标题',
      dataIndex: 'subtitle',
      key: 'subtitle',
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '链接',
      dataIndex: 'link',
      key: 'link',
    },
    {
      title: '排序',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 80,
    },
    {
      title: '状态',
      dataIndex: 'active',
      key: 'active',
      width: 80,
      render: (active: boolean) => (active ? '启用' : '禁用'),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_: any, record: Banner) => (
        <>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <div>
      <h2 className="page-header">Banner 管理</h2>
      <div className="action-buttons">
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          添加 Banner
        </Button>
        <Button icon={<SaveOutlined />} onClick={handleSaveAll}>
          保存所有更改
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={banners}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingBanner ? '编辑 Banner' : '添加 Banner'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        width={600}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="标题"
            name="title"
            rules={[{ required: true, message: '请输入标题' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="副标题"
            name="subtitle"
            rules={[{ required: true, message: '请输入副标题' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="描述"
            name="description"
            rules={[{ required: true, message: '请输入描述' }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item label="图片" name="image">
            <Input
              placeholder="图片路径或上传图片"
              value={uploadedImagePath}
              onChange={(e) => setUploadedImagePath(e.target.value)}
            />
          </Form.Item>
          <Form.Item label="上传图片">
            <Upload
              beforeUpload={handleUpload}
              showUploadList={false}
              accept="image/*"
            >
              <Button icon={<UploadOutlined />}>点击上传</Button>
            </Upload>
            {uploadedImagePath && (
              <div style={{ marginTop: 8 }}>
                <Image src={uploadedImagePath} alt="preview" width={200} />
              </div>
            )}
          </Form.Item>
          <Form.Item
            label="链接"
            name="link"
            rules={[{ required: true, message: '请输入链接' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="排序"
            name="sortOrder"
            rules={[{ required: true, message: '请输入排序' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="状态" name="active" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BannersPage;
